import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useProviderAccountsAccess } from 'pages/Finance/ProviderAccounts/hooks/useProviderAccountsAccess';
import { useNotify, useRedirect, useShowController } from 'react-admin';
import { DeleteButton, EditLinkButton } from 'shared/mui/Buttons';
import { DeleteDialog } from 'shared/mui/Dialogs';
import { ShowPage } from 'shared/react-admin/Pages/ShowPage/ShowPage';
import { CompanyShowDialog } from 'shared/widgets/CompanyShowDialog/CompanyShowDialog';
import { ProviderShowDialog } from 'shared/widgets/ProviderShowDialog/ProviderShowDialog';
import { Company } from 'types/embedded/company';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';
import { ProviderAccount } from 'types/embedded/providerAccount';
import { getId } from 'utils';

import { ProviderAccountsShowTable } from './ProviderAccountsShowTable/ProviderAccountsShowTable';

export const ProviderAccountsShow = () => {
  const { hasResourcePermissions } = usePermissions();
  const { hasActionsAccess } = useProviderAccountsAccess();
  const { record } = useShowController();
  const notify = useNotify();

  const canDelete =
    hasActionsAccess &&
    hasResourcePermissions(Resources.ProviderAccounts, [Permission.Full, Permission.Delete]);

  const redirect = useRedirect();

  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } =
    useRecordModal<ProviderAccount>();

  const {
    record: company,
    handleCloseRecordModal: handleCloseCompanyModal,
    openRecordModal: openCompanyModal,
    handleOpenRecordModal: handleOpenCompanyModal,
  } = useRecordModal<Partial<Company>>();

  const {
    record: provider,
    handleCloseRecordModal: handleCloseRecordModalProvider,
    openRecordModal: openRecordModalProvider,
    handleOpenRecordModal: handleOpenRecordModalProvider,
  } = useRecordModal<Provider>();

  return (
    <>
      <ShowPage
        listName="Настройки доступов"
        rightActionsSlot={[
          canDelete && (
            <DeleteButton
              key="delete-button"
              onClick={() => handleOpenRecordModal(record)}
              variant="outlined"
            />
          ),
          hasResourcePermissions(Resources.ProviderAccounts, [
            Permission.Full,
            Permission.Update,
          ]) && <EditLinkButton key="edit-button" label="Редактировать" variant="contained" />,
        ]}
        title="Просмотр настройки доступа"
      >
        <ProviderAccountsShowTable
          handleOpenCompanyModal={handleOpenCompanyModal}
          handleOpenRecordModalProvider={handleOpenRecordModalProvider}
        />
      </ShowPage>
      <DeleteDialog
        details={`Настройка доступа ID ${getId(record?.id)} будет удалена без возможности восстановления.`}
        onClose={handleCloseRecordModal}
        onSuccess={() => {
          redirect('list', Resources.ProviderAccounts);
          notify(`Настройка доступа ID ${getId(record?.id)} удалена`, {
            type: 'success',
          });
        }}
        open={openRecordModal}
        record={record}
        title="Удаление настройки доступа"
      />
      {company && (
        <CompanyShowDialog
          company={company}
          onClose={handleCloseCompanyModal}
          open={openCompanyModal}
        />
      )}
      {provider && (
        <ProviderShowDialog
          onClose={handleCloseRecordModalProvider}
          open={openRecordModalProvider}
          provider={provider}
        />
      )}
    </>
  );
};
